import { OrthographicCamera, type Raycaster, type Vector2 } from 'three'
import type { CameraSpec } from '../core/levelData'
import { convertPosition, convertQuaternion } from '../core/unity'

/**
 * Port of `CameraResizer` (`OnLevelLoaded` + `AdjustCameraSize`).
 *
 * The gameplay camera is orthographic. On level load it takes its position,
 * rotation and `orthographicSize` straight from the level's `CameraData`, then
 * `AdjustCameraSize` widens the size on screens narrower than the reference
 * aspect so the level never gets cropped horizontally:
 *
 *     if (screenAspect < targetAspect)
 *         orthographicSize *= targetAspect / screenAspect
 *
 * `referenceWidth` / `referenceHeight` are `[SerializeField]`s whose values
 * IL2CPP dropped. 1080×1920 is the near-certain authoring resolution for a
 * portrait mobile title; it is exposed here so tuning can correct it.
 */
export interface CameraTuning {
  referenceWidth: number
  referenceHeight: number
}

export const DEFAULT_CAMERA_TUNING: CameraTuning = {
  referenceWidth: 1080,
  referenceHeight: 1920,
}

/**
 * `Main Camera`'s clip planes, straight out of `Gameplay.unity`.
 *
 * The near plane is **negative**, which an orthographic camera is allowed to be
 * and this one has to be. The camera sits at y 17.4 looking down at 65 degrees,
 * so the far end of a level — the bottom of the screen — is already *behind*
 * the plane through the camera's own position: the tunnel mouth down there
 * measures −0.3 along the view axis. With the usual small positive near it gets
 * sliced open and the ground shows through the hole.
 */
const NEAR_CLIP = -100
const FAR_CLIP = 100

export class GameCamera {
  readonly camera: OrthographicCamera
  private baseOrthographicSize = 5
  private tuning: CameraTuning

  constructor(tuning: CameraTuning = DEFAULT_CAMERA_TUNING) {
    this.tuning = tuning
    this.camera = new OrthographicCamera(-1, 1, 1, -1, NEAR_CLIP, FAR_CLIP)
  }

  /** `CameraResizer.OnLevelLoaded`. */
  applyLevel(spec: CameraSpec): void {
    convertPosition(spec.position, this.camera.position)
    convertQuaternion(spec.rotation, this.camera.quaternion)
    this.baseOrthographicSize = spec.orthographicSize
    this.camera.updateMatrixWorld()
  }

  /**
   * Aim `raycaster` through the NDC point `pointer`, for picking.
   *
   * 📐 **The ray has to start at the near plane, or the buses at the bottom of
   * the screen cannot be clicked.** `Raycaster.setFromCamera` starts an
   * orthographic ray at the NDC depth `(near + far) / (near - far)`, which for
   * the symmetric −100/100 pair above is **0** — the plane through the camera's
   * own position, not the near plane. Everything the negative near was added to
   * keep visible lies *behind* that origin.
   *
   * ⚠️ And a hit behind the origin is unreachable, whatever window the
   * raycaster is given: `Ray.intersectTriangle` returns `null` on `QdN < 0`
   * (`three/src/math/Ray.js:600-607`) before `near`/`far` are ever consulted,
   * and `checkIntersection` then measures `origin.distanceTo(point)`, which
   * cannot be negative. So widening `raycaster.near` to the camera's own
   * negative near — the obvious fix, and the one first tried here — changes
   * **nothing**: measured over all forty levels it left the same 53 buses
   * unclickable, on 17 different levels, level 19's five among them.
   *
   * What works is moving the origin to where the camera's near plane actually
   * is. `zNdc = 2t / (far - near) - (far + near) / (far - near)` with
   * `zView = -t` puts the near plane at ray distance `t = near`, so stepping
   * the origin along the ray by `camera.near` lands on it, and the frustum is
   * then the whole positive window `[0, far - near]`. Same 40-level sweep:
   * **0 buses unreachable**.
   *
   * The bug this fixes was reported on level 19, whose lot reaches furthest
   * down the screen — the buses at `boxPosition.z ≈ -31.8` have hit boxes the
   * old ray origin sat *inside*, so their front faces were behind it and their
   * back faces were culled. `pickRay.test.ts` locks all of it.
   */
  aimRaycaster(raycaster: Raycaster, pointer: Vector2): void {
    raycaster.setFromCamera(pointer, this.camera)
    raycaster.ray.origin.addScaledVector(raycaster.ray.direction, this.camera.near)
    raycaster.near = 0
    raycaster.far = this.camera.far - this.camera.near
  }

  /** `CameraResizer.AdjustCameraSize`. Call on resize. */
  resize(pixelWidth: number, pixelHeight: number): void {
    const targetAspect = this.tuning.referenceWidth / this.tuning.referenceHeight
    const screenAspect = pixelWidth / pixelHeight

    let size = this.baseOrthographicSize
    if (screenAspect < targetAspect) size *= targetAspect / screenAspect

    this.camera.top = size
    this.camera.bottom = -size
    this.camera.left = -size * screenAspect
    this.camera.right = size * screenAspect
    this.camera.updateProjectionMatrix()
  }
}
